/**
 * Store for managing custom domain state and operations.
 */

import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  purpose?: string;
}

interface CustomDomain {
  id: string;
  user_id: string;
  domain: string;
  verification_status: 'pending' | 'verified' | 'failed' | 'misconfigured' | 'not_present_on_vercel';
  vercel_domain_id: string | null;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

interface DomainsState {
  domains: CustomDomain[];
  isLoading: boolean;
  isAdding: boolean;
  isVerifying: boolean;
  isRemoving: boolean;
  error: string | null;
  dnsRecords: DnsRecord[];

  // Actions
  fetchDomains: () => Promise<void>;
  addDomain: (domain: string) => Promise<{ success: boolean; dnsRecords?: DnsRecord[] }>;
  verifyDomain: (domainId: string) => Promise<{ success: boolean; verified: boolean; dnsRecords?: DnsRecord[] }>;
  removeDomain: (domainId: string) => Promise<boolean>;
  clearError: () => void;
  resetDomains: () => void;
}

export const useDomainsStore = create<DomainsState>((set) => ({
  domains: [],
  isLoading: false,
  isAdding: false,
  isVerifying: false,
  isRemoving: false,
  error: null,
  dnsRecords: [],

  fetchDomains: async () => {
    logger.info('DomainsStore', 'Fetching custom domains');
    set({ isLoading: true, error: null });

    try {
      const response = await api.get('/api/domains');
      
      if (response.data.success) {
        set({ domains: response.data.domains, isLoading: false });
        logger.info('DomainsStore', 'Domains fetched successfully', {
          count: response.data.domains.length,
        });
      } else {
        throw new Error(response.data.error || 'Failed to fetch domains');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch domains';
      logger.error('DomainsStore', 'Error fetching domains', { error: errorMessage });
      set({ error: errorMessage, isLoading: false, domains: [] });
    }
  },

  addDomain: async (domain: string) => {
    logger.info('DomainsStore', 'Adding custom domain', { domain });
    set({ isAdding: true, error: null, dnsRecords: [] });

    try {
      const response = await api.post('/api/domains/add', { domain });

      if (response.data.success) {
        // Add the new domain to the list
        const newDomain = response.data.domain;
        set((state) => ({
          domains: [newDomain, ...state.domains],
          isAdding: false,
          dnsRecords: response.data.dnsRecords || [],
        }));

        logger.info('DomainsStore', 'Domain added successfully', { domain });
        return { success: true, dnsRecords: response.data.dnsRecords };
      } else {
        throw new Error(response.data.error || 'Failed to add domain');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error || 
        (error instanceof Error ? error.message : 'Failed to add domain');
      logger.error('DomainsStore', 'Error adding domain', { domain, error: errorMessage });
      set({ error: errorMessage, isAdding: false });
      return { success: false };
    }
  },

  verifyDomain: async (domainId: string) => {
    logger.info('DomainsStore', 'Verifying domain', { domainId });
    set({ isVerifying: true, error: null });

    try {
      const response = await api.post('/api/domains/verify', { domainId });

      if (response.data.success) {
        // Update the domain in the list
        const updatedDomain = response.data.domain;
        set((state) => ({
          domains: state.domains.map((d) => (d.id === domainId ? updatedDomain : d)),
          isVerifying: false,
          dnsRecords: response.data.dnsRecords || [],
        }));

        logger.info('DomainsStore', 'Domain verification completed', {
          domainId,
          verified: response.data.verified,
        });

        return {
          success: true,
          verified: response.data.verified,
          dnsRecords: response.data.dnsRecords,
        };
      } else {
        throw new Error(response.data.error || 'Verification failed');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error ||
        (error instanceof Error ? error.message : 'Verification failed');
      logger.error('DomainsStore', 'Error verifying domain', { domainId, error: errorMessage });
      set({ error: errorMessage, isVerifying: false });
      return { success: false, verified: false };
    }
  },

  removeDomain: async (domainId: string) => {
    logger.info('DomainsStore', 'Removing domain', { domainId });
    set({ isRemoving: true, error: null });

    try {
      const response = await api.delete('/api/domains/remove', { data: { domainId } });

      if (response.data.success) {
        // Remove the domain from the list
        set((state) => ({
          domains: state.domains.filter((d) => d.id !== domainId),
          isRemoving: false,
        }));

        logger.info('DomainsStore', 'Domain removed successfully', { domainId });
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to remove domain');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error ||
        (error instanceof Error ? error.message : 'Failed to remove domain');
      logger.error('DomainsStore', 'Error removing domain', { domainId, error: errorMessage });
      set({ error: errorMessage, isRemoving: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),

  resetDomains: () => {
    logger.info('DomainsStore', 'Resetting domains state');
    set({
      domains: [],
      isLoading: false,
      isAdding: false,
      isVerifying: false,
      isRemoving: false,
      error: null,
      dnsRecords: [],
    });
  },
}));

